import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';

const AuthCallback = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('processing');
  const [message, setMessage] = useState('');
  const [subMessage, setSubMessage] = useState('');

  useEffect(() => {
    const handleCallback = async () => {
      try {
        const code = searchParams.get('code');
        const state = searchParams.get('state');
        const error = searchParams.get('error');
        const errorDescription = searchParams.get('error_description');

        if (error) {
          setStatus('error');
          setMessage(t('authCallback.authDenied'));
          setSubMessage(errorDescription || t('authCallback.authDeniedSub'));
          return;
        }

        if (!code || !state) {
          setStatus('error');
          setMessage(t('authCallback.invalidCallback'));
          setSubMessage(t('authCallback.invalidCallbackSub'));
          return;
        }

        // Check both storage locations (Capacitor uses localStorage, web uses sessionStorage)
        const savedState = sessionStorage.getItem('tiktok_csrf_state') || localStorage.getItem('tiktok_csrf_state');
        if (!savedState || savedState !== state) {
          setStatus('error');
          setMessage(t('authCallback.securityFailed'));
          setSubMessage(t('authCallback.securityFailedSub'));
          return;
        }
        sessionStorage.removeItem('tiktok_csrf_state');
        localStorage.removeItem('tiktok_csrf_state');

        setMessage(t('authCallback.verifying'));

        const isSandbox = localStorage.getItem('tiktok_sandbox') === '1';
        const { data, error: fnError } = await supabase.functions.invoke('tiktok-auth', {
          body: { code, redirect_uri: import.meta.env.VITE_TIKTOK_REDIRECT_URI || 'https://noirock.app/auth/callback', sandbox: isSandbox }
        });
        localStorage.removeItem('tiktok_sandbox');

        // Inspection sandbox : ce que video.list renvoie (ouvrir la console).
        if (isSandbox) {
          console.log('[sandbox] video.list →', data?.videos);
          if (data?.videosError) console.warn('[sandbox] video.list error →', data.videosError);
          try { localStorage.setItem('tiktok_sandbox_videos', JSON.stringify({ videos: data?.videos ?? null, error: data?.videosError ?? null })); } catch { /* ignore */ }
        }

        if (fnError || !data?.success) {
          const errMsg = data?.error || fnError?.message || '';

          // Check if the error is about creator not found in database
          if (errMsg.toLowerCase().includes('not found') || errMsg.toLowerCase().includes('not registered') || errMsg.toLowerCase().includes('no creator')) {
            setStatus('error');
            setMessage(t('authCallback.notRegistered'));
            setSubMessage(t('authCallback.notRegisteredSub'));
            return;
          }

          setStatus('error');
          setMessage(t('authCallback.authFailed'));
          setSubMessage(errMsg || t('authCallback.authFailedSub'));
          return;
        }

        // Set Supabase Auth session (real JWT with refresh)
        if (data.session?.access_token && data.session?.refresh_token) {
          await supabase.auth.setSession({
            access_token: data.session.access_token,
            refresh_token: data.session.refresh_token,
          });
        }

        // Keep minimal localStorage for quick display (avatar, username) — NOT for auth
        localStorage.setItem('creator_profile', JSON.stringify({
          tiktok_id: data.creator.tiktok_id,
          username: data.creator.username,
          display_name: data.creator.tiktok_display_name,
          avatar_url: data.creator.tiktok_avatar_url,
        }));

        // TikTok data (stats + recent videos) — pour la page créateur (montre l'usage
        // des scopes user.info.stats + video.list à la review).
        try {
          localStorage.setItem('creator_tiktok', JSON.stringify({
            stats: data.stats ?? null,
            videos: Array.isArray(data.videos) ? data.videos : [],
            bio: data.creator?.bio ?? null,
          }));
        } catch { /* ignore */ }

        setStatus('success');
        setMessage(`Welcome, ${data.creator.tiktok_display_name || data.creator.username}!`);

        setTimeout(() => {
          // Connu (rattaché à une agence) → portail ; inconnu → espace public créateur.
          navigate(data.known === false ? '/my/public-dashboard' : '/my/dashboard', { replace: true });
        }, 1500);

      } catch (err) {
        console.error('Auth callback error:', err);
        setStatus('error');
        setMessage(t('authCallback.unexpectedError'));
        setSubMessage(t('authCallback.unexpectedErrorSub'));
      }
    };

    handleCallback();
  }, [searchParams, navigate]);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center"
      style={{ backgroundColor: 'var(--color-background)', fontFamily: 'Inter, sans-serif' }}
    >
      <link
        href="https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&family=Sora:wght@400;500;600;700;800&display=swap"
        rel="stylesheet"
      />

      <div className="w-full max-w-sm px-4">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#E5202A] flex items-center justify-center">
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M3 3h6v6H3zM11 3h6v6h-6zM3 11h6v6H3zM11 11h6v6h-6z" fill="white" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold text-white tracking-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
              NoiRock
            </h1>
          </div>
        </div>

        {/* Status Card */}
        <div className="rounded-2xl p-7 border border-white/8 text-center" style={{ backgroundColor: '#111111' }}>

          {/* Processing */}
          {status === 'processing' && (
            <>
              <div className="mb-5 flex justify-center">
                <div className="w-10 h-10 border-2 border-[#00F2EA] border-t-transparent rounded-full animate-spin" />
              </div>
              <p className="text-white/60 text-sm">{message}</p>
            </>
          )}

          {/* Success */}
          {status === 'success' && (
            <>
              <div className="mb-5 flex justify-center">
                <div className="w-10 h-10 rounded-full bg-green-500/20 border border-green-500/40 flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#4ade80" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </div>
              </div>
              <p className="text-green-400 text-sm font-medium mb-1">{message}</p>
              <p className="text-white/30 text-xs">{t('authCallback.redirecting')}</p>
            </>
          )}

          {/* Error */}
          {status === 'error' && (
            <>
              <div className="mb-5 flex justify-center">
                <div className="w-10 h-10 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </div>
              </div>
              <p className="text-red-400 text-sm font-medium mb-2">{message}</p>
              {subMessage && (
                <p className="text-white/30 text-xs leading-relaxed mb-4 px-2">{subMessage}</p>
              )}
              <button
                onClick={() => navigate('/my/login', { replace: true })}
                className="px-5 py-2.5 rounded-xl bg-white/5 border border-white/10 text-white/60 text-sm hover:bg-white/10 hover:text-white transition-all"
              >
                {t('authCallback.backToLogin')}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AuthCallback;
